import { Suspense, useEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Toaster } from "sonner";
import "@/i18n";
import { PageState } from "@/components/ui/PageState";
import { PredictionProvider } from "@/features/prediction/public/context";
import { ThemeProvider } from "@/lib/theme/ThemeProvider";
import { useTheme } from "@/lib/theme/useTheme";
import { AccountGate } from "@/features/account";
import { useSessionLease } from "@/lib/api/useSessionLease";

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <AccountGate>{(identity) => <RuntimeProviders key={identity}>{children}</RuntimeProviders>}</AccountGate>
    </ThemeProvider>
  );
}

function RuntimeProviders({ children }: { children: ReactNode }) {
  const owner = useSessionLease();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );
  const { theme } = useTheme();
  useEffect(() => () => queryClient.clear(), [queryClient]);
  useEffect(() => { if (owner.isCurrent()) void queryClient.invalidateQueries(); }, [owner, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <PredictionProvider>
          <Suspense fallback={<PageState kind="loading" />}>
            {children}
          </Suspense>
        </PredictionProvider>
        <Toaster theme={theme} richColors position="bottom-right" closeButton />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
